<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import ViewStep from '@/components/step/ViewStep.vue'
import { StepChangeKind, StepDiffDataQuery, StepDiffDataQueryVariables } from '@/generated/graphql'

const props = defineProps<{
  bopId?: string
  moduleId?: string
  stepId?: string
  removedStepId?: string
}>()

const { t } = useI18n()

const { result, loading } = useQuery<StepDiffDataQuery, StepDiffDataQueryVariables>(
  gql`
    query StepDiffData($bopId: ID!, $moduleId: ID!, $stepId: ID!) {
      product {
        stepDiff(bopId: $bopId, moduleId: $moduleId, stepId: $stepId) {
          kind
          changedFields
          currentStep {
            id
          }
          previousStep {
            id
          }
        }
      }
    }
  `,
  () => ({ bopId: props.bopId ?? '', moduleId: props.moduleId ?? '', stepId: props.stepId ?? '' }),
  () => ({ enabled: !props.removedStepId && !!(props.bopId && props.moduleId && props.stepId) }),
)

const diff = computed(() => result.value?.product.stepDiff)
const changedFields = computed(() => diff.value?.changedFields ?? [])
const isNewStep = computed(() => diff.value?.kind === StepChangeKind.Added)
const currentStepId = computed(() => diff.value?.currentStep.id)
const previousStepId = computed(() => props.removedStepId ?? diff.value?.previousStep?.id)

// The panels mount hidden behind one skeleton until every step has loaded, so the content appears in one go
const previousLoaded = ref(false)
const currentLoaded = ref(false)
const ready = computed(() => {
  if (props.removedStepId) return previousLoaded.value
  if (!diff.value) return false
  return (
    (!previousStepId.value || previousLoaded.value) && (!currentStepId.value || currentLoaded.value)
  )
})
</script>

<template>
  <v-skeleton-loader v-if="!ready" type="article" />
  <template v-if="removedStepId || diff">
    <v-row v-show="ready" dense>
      <v-col cols="6" class="step-diff-panel step-diff-panel--previous">
        <h3 class="text-center mb-4">{{ t('component.stepChangeBanner.previousVersion') }}</h3>
        <view-step
          v-if="previousStepId"
          :step-id="previousStepId"
          :highlighted-fields="changedFields"
          highlight-color="error"
          hide-font-size
          @loaded="previousLoaded = true"
        />
        <div v-else class="text-grey text-center mt-4">
          {{
            isNewStep
              ? t('component.stepChangeBanner.newStep')
              : t('component.stepChangeBanner.noPreviousVersion')
          }}
        </div>
      </v-col>
      <v-col cols="6" class="step-diff-panel step-diff-panel--current">
        <h3 class="text-center mb-4">{{ t('component.stepChangeBanner.currentVersion') }}</h3>
        <div v-if="removedStepId" class="text-grey text-center mt-4">
          {{ t('component.stepChangeBanner.removedStep') }}
        </div>
        <view-step
          v-else-if="currentStepId"
          :step-id="currentStepId"
          :highlighted-fields="changedFields"
          highlight-color="success"
          hide-font-size
          @loaded="currentLoaded = true"
        />
      </v-col>
    </v-row>
  </template>
</template>

<style scoped lang="scss">
.step-diff-panel {
  border-radius: 0.5em;
  padding: 1em;

  :deep(.step-title-row) {
    --step-title-font-size: 1.25rem;
  }

  :deep(h2) {
    font-size: 1.1rem;
  }

  &--previous {
    border: 1px solid rgba(var(--v-theme-error), 0.3);
  }

  &--current {
    border: 1px solid rgba(var(--v-theme-success), 0.3);
  }
}
</style>
